import { Dispatch } from "@reduxjs/toolkit";
import { SEVERITIES } from "../../../data/constants";
import { showAlert } from "../../../redux/alertSlice";

/**
 * Router state PostInterviewForm sets when a locked form opened at /:id/edit is sent to the
 * read-only /:id view, so PostIFApproval shows the "can no longer be edited" note (Bug 84564).
 * One key for both sides, so a rename cannot silently drop the note.
 */
export const PIF_NOT_EDITABLE_STATE_KEY = "pifNotEditable";
export type PifRecordRouteState = { [PIF_NOT_EDITABLE_STATE_KEY]?: boolean };

/**
 * Copies a PostIF's own read-only route to the clipboard and reports the result. Shared by the
 * supervisor's done screen and the approvers' done screens (Bug 83237), so the two cannot drift.
 *
 * The link is `/hiring/postif/:id`, never `window.location.href`: the supervisor's done screen is a
 * STATE of /hiring/postif/new, and the approver's may be the emailed /sign/:token route, which is
 * spent after one use - copying either handed out a link that did not open this record. `/:id`
 * admits the creator (sProc_Portal_PostIF_CanAct_S 'open'), while `/:id/edit` is locked once the
 * status is 'submitted'.
 *
 * Every path reports back. A silent copy and a missing clipboard API look identical to the user,
 * which is why the supervisor's version once read as "not working" even where it did copy.
 */
export const copyPifRecordLink = (postIfId: number | null | undefined, dispatch: Dispatch): void => {
  if (!postIfId) {
    dispatch(showAlert({ message: "This form has no shareable link yet.", severity: SEVERITIES.ERROR }));
    return;
  }

  const url = `${window.location.origin}/hiring/postif/${postIfId}`;
  const fallback = () =>
    dispatch(showAlert({ message: `Couldn't copy automatically. The link is ${url}`, severity: SEVERITIES.WARNING }));

  // `navigator.clipboard` is undefined on a non-secure origin, and writeText rejects when the
  // document isn't focused or permission is denied - both fall back to showing the link.
  const copying = navigator.clipboard?.writeText(url);
  if (!copying) {
    fallback();
    return;
  }
  copying.then(
    () => dispatch(showAlert({ message: "Link copied to clipboard.", severity: SEVERITIES.SUCCESS })),
    fallback,
  );
};
